/**
 * One-time move of the Cognito session from localStorage to cookies (SA-8857).
 *
 * Amplify is switching to cookie storage on the shared parent domain so the new portal, on a
 * sibling subdomain, sees the same session. Without this patch every signed-in user would be
 * logged out on deploy, because their tokens are in a store Amplify no longer reads.
 *
 * Runs before `Auth.configure`. Safe to call on every boot: it does nothing once the cookies
 * exist, and nothing when there was never a session. The caller must act on the result — see
 * `SessionMigrationResult`.
 */

/**
 * - `migrated` — the session was copied, verified and localStorage cleared.
 * - `not-needed` — no cookie domain, no session to move, or the cookies are already there.
 * - `failed` — a cookie did not persist. Every cookie written has been rolled back and
 *   localStorage is untouched, so the session is still readable *from localStorage only*.
 *   The caller MUST keep Amplify on localStorage for this boot; configuring it for cookies
 *   would hand it an empty store and sign out a user who is in fact signed in.
 */
export type SessionMigrationResult = 'migrated' | 'not-needed' | 'failed';

// Amplify v5 and v6 both key tokens this way. v5 also writes `userData` and v6 `signInDetails`;
// copying userData across costs nothing and saves the old portal a re-fetch.
const TOKEN_KEYS = [
  'idToken',
  'accessToken',
  'refreshToken',
  'clockDrift',
  'userData'
];

const LAST_AUTH_USER = 'LastAuthUser';

// Amplify's own CookieStorage (amazon-cognito-identity-js) defaults to 365 days and writes
// through js-cookie, which serialises `expires` rather than `max-age`. Matching it matters: a
// session cookie would die at browser close, where the localStorage it replaces would not.
const COOKIE_LIFETIME_DAYS = 365;

const EXPIRED = 'Thu, 01 Jan 1970 00:00:00 GMT';

/**
 * js-cookie's name encoding, replicated so these cookies carry byte-identical names to the ones
 * Amplify writes later. A username containing `+` encodes differently under plain
 * `encodeURIComponent`, which would leave two cookies for one logical key.
 */
function encodeName(name: string): string {
  return encodeURIComponent(name).replace(
    /%(23|24|26|2B|5E|60|7C)/g,
    decodeURIComponent
  );
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    // A malformed cookie set by something else on this domain must not abort the migration.
    return value;
  }
}

/**
 * The whole cookie string parsed once. The read-back pass checks six keys, and rescanning
 * `document.cookie` per key is avoidable work on the boot path.
 */
function readCookieJar(): { [name: string]: string } {
  const jar: { [name: string]: string } = {};
  const raw = document.cookie;

  if (!raw) {
    return jar;
  }

  for (const pair of raw.split(';')) {
    const eq = pair.indexOf('=');
    if (eq < 0) {
      continue;
    }
    jar[safeDecode(pair.slice(0, eq).trim())] = safeDecode(pair.slice(eq + 1));
  }

  return jar;
}

function writeCookie(name: string, value: string, domain: string): void {
  const expires = new Date(
    Date.now() + COOKIE_LIFETIME_DAYS * 864e5
  ).toUTCString();

  document.cookie = `${encodeName(name)}=${encodeURIComponent(
    value
  )}; domain=${domain}; path=/; expires=${expires}; secure; samesite=lax`;
}

function deleteCookie(name: string, domain: string): void {
  document.cookie = `${encodeName(
    name
  )}=; domain=${domain}; path=/; expires=${EXPIRED}; secure; samesite=lax`;
}

/**
 * @param clientId the Cognito app client id, which namespaces every key
 * @param domain the shared parent domain, e.g. `.salvehealthtest.net`; null locally
 */
export function migrateSessionToCookieStorage(
  clientId: string,
  domain: string | null
): SessionMigrationResult {
  if (!clientId || !domain) {
    return 'not-needed';
  }

  const prefix = `CognitoIdentityServiceProvider.${clientId}`;
  const lastAuthUserKey = `${prefix}.${LAST_AUTH_USER}`;

  // Already migrated, here or in another tab. This guard is only trustworthy because the key it
  // checks is written last and rolled back on failure, so its presence means a complete set.
  if (readCookieJar()[lastAuthUserKey]) {
    return 'not-needed';
  }

  let username: string | null;
  try {
    username = localStorage.getItem(lastAuthUserKey);
  } catch {
    // Storage can throw outright when blocked. Nothing to migrate, and nothing broken.
    return 'not-needed';
  }

  if (!username) {
    return 'not-needed';
  }

  const moved: { key: string; value: string }[] = [];

  for (const tokenKey of TOKEN_KEYS) {
    const key = `${prefix}.${username}.${tokenKey}`;
    const value = localStorage.getItem(key);
    if (value !== null) {
      moved.push({ key, value });
    }
  }

  // Last, deliberately: the "already migrated" guard above keys off this one, so it must never
  // exist alongside a half-written set.
  moved.push({ key: lastAuthUserKey, value: username });

  for (const entry of moved) {
    writeCookie(entry.key, entry.value, domain);
  }

  // Read every cookie back before dropping the originals. A cookie that silently failed to
  // write -- too large, blocked, wrong domain -- must cost a re-login, never a stranded session.
  const jar = readCookieJar();
  const allPersisted = moved.every((entry) => jar[entry.key] === entry.value);

  if (!allPersisted) {
    // Roll back everything, guard key included, so the next boot retries rather than skipping
    // forever while the session sits untouched in localStorage.
    for (const entry of moved) {
      deleteCookie(entry.key, domain);
    }
    return 'failed';
  }

  for (const entry of moved) {
    localStorage.removeItem(entry.key);
  }

  return 'migrated';
}
